import React, { useEffect, useRef, useState } from 'react';
import { buildURL } from '../../../utils/stringExtensions';
import { ICON_TYPES } from '@shared/constants/icon-types';
import GoogleFontsIcon from '@shared/components/icons/GoogleFontsIcon';
import cx from 'classnames';
import { useNavigate } from 'react-router-dom';
import { throttle } from 'lodash';

const ITEM_HEIGHT = 36;
const MIN_ITEMS_FOR_SCROLL = 5;

const SCROLL_THRESHOLD = 16;
const SCROLL_STEP = ITEM_HEIGHT * MIN_ITEMS_FOR_SCROLL;

const SCROLL_DIRECTION = {
  UP: 'up',
  DOWN: 'down'
};

/**
 * @typedef {object} CheckPointListAppraisalOrgUnitCheckPoints
 * @property {string} [DataCollectionDate]
 * @property {boolean} [IsDraft]
 */

/**
 * @typedef {object} CheckPointListAppraisalOrgUnitAppraisal
 * @property {number} Id
 */

/**
 * @typedef {object} CheckPointListAppraisalOrgUnit
 * @property {CheckPointListAppraisalOrgUnitCheckPoints[]} CheckPoints
 * @property {CheckPointListAppraisalOrgUnitAppraisal} Appraisal
 */

/**
 * @typedef {object} CheckPointListProps
 * @property {number} orgUnitId
 * @property {CheckPointListAppraisalOrgUnit} appraisalOrgUnit
 * @property {(appraisalId: number, checkpointId: number) => void} navigateToAddCheckPoint
 */

/**
 * @param {CheckPointListProps} props
 */
const CheckPointList = props => {
  const { orgUnitId, appraisalOrgUnit, navigateToAddCheckPoint } = props;
  const { CheckPoints: checkpoints, Appraisal: appraisal } = appraisalOrgUnit;

  const scrollContainerRef = useRef(null);
  const [isAtTop, setIsAtTop] = useState(true);
  const [isAtBottom, setIsAtBottom] = useState(false);

  const hasScrollControls = checkpoints.length > MIN_ITEMS_FOR_SCROLL;

  const navigate = useNavigate();

  const throttledHandleOnScroll = useRef(
    throttle(
      () => {
        const container = scrollContainerRef.current;
        if (!container) {
          return;
        }

        const { scrollTop, scrollHeight, clientHeight } = container;

        setIsAtTop(scrollTop < SCROLL_THRESHOLD);
        setIsAtBottom(scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD);
      },
      100,
      { leading: true, trailing: true }
    )
  );

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const throttledOnScrollFn = throttledHandleOnScroll.current;

    container.addEventListener('scroll', throttledOnScrollFn);
    throttledOnScrollFn();

    return () => {
      container.removeEventListener('scroll', throttledOnScrollFn);
      throttledOnScrollFn.cancel();
    };
  }, [hasScrollControls]);

  const handleScrollButtonClick = direction => {
    const container = scrollContainerRef.current;

    container.scrollBy({
      top: direction === SCROLL_DIRECTION.DOWN ? SCROLL_STEP : -SCROLL_STEP
    });
  };

  const handleCheckPointClick = checkpoint => {
    if (checkpoint.IsDraft) {
      navigateToAddCheckPoint(appraisal.Id, checkpoint.Id);
      return;
    }

    navigate(
      buildURL(['/check-points', orgUnitId, 'view-check-point', appraisal.Id, checkpoint.Id])
    );
  };

  return (
    <div className={cx('check-point-list', { 'center-items': !hasScrollControls })}>
      <div
        ref={scrollContainerRef}
        className='check-point-list-items'
        data-testid='check-point-list-items'>
        {checkpoints.map((checkpoint, index) => (
          <a key={index} onClick={() => handleCheckPointClick(checkpoint)}>
            {index + 1}. {checkpoint.DataCollectionDate || 'TBD'}
            {checkpoint.IsDraft ? ' - Draft' : ''}
          </a>
        ))}
      </div>

      {hasScrollControls && (
        <div
          className='check-point-list-icon-container'
          data-testid='check-point-list-icon-container'>
          <button onClick={() => handleScrollButtonClick(SCROLL_DIRECTION.UP)} disabled={isAtTop}>
            <GoogleFontsIcon
              className={cx('material-icons', { disabled: isAtTop })}
              iconType={ICON_TYPES.KeyboardArrowUp}
            />
          </button>

          <button
            onClick={() => handleScrollButtonClick(SCROLL_DIRECTION.DOWN)}
            disabled={isAtBottom}>
            <GoogleFontsIcon
              className={cx('material-icons', { disabled: isAtBottom })}
              iconType={ICON_TYPES.KeyboardArrowDown}
            />
          </button>
        </div>
      )}
    </div>
  );
};

export default CheckPointList;
